/**
 * forgeSpatialFocusAdapter.js
 *
 * The ONE place a spatial-focus render plan touches Forge. `useSpatialFocus` resolves,
 * `spatialFocusArbitrator` decides, this paints - and, just as importantly, it RECORDS what
 * actually reached the screen so the legend describes the viewer rather than the request.
 *
 * It writes no rendering code of its own. Every mutation goes through a primitive that already
 * ships:
 *
 *   - isolate / de-isolate  `mainExtension.isolateDbIds(dbIds, model)`  (mainExtension.js:1451)
 *   - theming               `setThemingColor(viewer, dbId, vec4, model)` (forgeUtils.js:1272)
 *   - scene reset           `clearThemingColors(viewer)`                (forgeUtils.js:1268)
 *   - element fit           `zoomToFit(viewer, dbIds, model)`           (forgeUtils.js:347)
 *   - point fit             `forgeService.zoomToPoint3dViewer(point)`   (forgeService.js:4111)
 *   - markers               `forgeService.showSpatialPointMarkers(...)` (forgeService.js:4552)
 *   - pulse                 `startTaskIsolationPulse(...)`              (taskIsolationPaint.js:198)
 *
 * DELIBERATE NON-REUSE - `applyDbIdColorMap` (forgeUtils.js:1295). It is the closest existing
 * composition, but it is single-model by construction (`extension.isolateDbIds(dbIds)` with no
 * model, `clearThemingColors` which only reaches `viewer.model`) and its `SET_THEMED_DB_IDS`
 * bookkeeping is keyed by BARE dbId. Plan C's geometry identity is `(normalized model URN, dbId)`
 * and bare-dbId bookkeeping is explicitly forbidden, so this module keeps its own
 * `viewer -> URN -> dbId` ledger and calls the model-aware primitives underneath it directly.
 *
 * Gate: `.codex/tests/spatial-focus-lifecycle.assert.cjs`
 */

import store from '@/store';
import { $hexToVector4 } from '@/utils';
import { clearThemingColors, setThemingColor, zoomToFit } from '@/utils/forgeUtils';
import { warn } from '@/utils/debugLogger';
import { lightenHex } from '@/utils/colorUtils';
import {
  startTaskIsolationPulse,
  TASK_ISOLATION_NATURAL_FLASH_COLOR,
  TASK_ISOLATION_PULSE_LIGHTEN,
} from '@/features/Task/utils/taskIsolationPaint';
import { normalizeModelUrn } from './spatialFocusArbitrator';
import { activateSpatialMarkerSubject } from './spatialMarkerActivation';

/** NUL cannot appear in a URN, so it cannot forge a collision between two identities. */
const IDENTITY_SEPARATOR = '\u0000';

const STATE_CONTEXT = 'context';

/** A marker the CML channel refused, or accepted from the plan but did not place on screen. */
const DIAGNOSTIC_MARKER_BLOCKED = 'markerChannelBlocked';

/**
 * A marker address claimed by more than one stable subject (a MIXED point), so no single domain id
 * is true for it. It still renders, keyed by its address - but it is not subject-addressable, and
 * saying so beats stamping one of the two subjects' ids onto a mesh that represents both.
 */
const DIAGNOSTIC_MARKER_SUBJECT_AMBIGUOUS = 'markerSubjectAmbiguous';

/**
 * The token this feature claims on the shared point-marker layer (`TAGS.cmlMarkerOwner`, forwarded
 * by `showSpatialPointMarkers` and nulled by `clearCmlPointMarkers`). Exported so a caller can
 * recognise a spatial-focus-owned layer.
 */
export const SPATIAL_FOCUS_MARKER_OWNER = 'spatialFocus';

/**
 * More than one model bucket had to be isolated. The extension holds ONE `isolatedDbids` set, so
 * the last bucket owns it; the earlier buckets stay themed but their visibility is not guaranteed.
 * Reported rather than hidden - multi-model rendering is out of scope for this delivery.
 */
const DIAGNOSTIC_MULTI_MODEL = 'multiModelIsolate';

/** Several target points had to share one camera move, so the frame is a centroid, not a fit. */
const DIAGNOSTIC_POINT_FIT_APPROXIMATE = 'pointFitApproximate';

/** The scene had something to frame but the viewer offered no usable fit primitive. */
const DIAGNOSTIC_FIT_UNAVAILABLE = 'fitUnavailable';

/**
 * What this adapter painted, per viewer. A WeakMap so a disposed viewer takes its ledger with it.
 *
 *   { themed: Map<'urn\0dbId', { model, dbId }>, markers: number, stopPulse: fn|null }
 *
 * Keyed by URN + dbId, never by bare dbId: the same dbId in two loaded models is two identities,
 * and un-theming one of them must not silently un-theme the other.
 */
const ledgerByViewer = new WeakMap();

function forgeService() {
  return store.state.forgeService || null;
}

function mainExtension() {
  const service = forgeService();
  return service && typeof service.getMainExtension === 'function'
    ? service.getMainExtension()
    : null;
}

function identityKey(modelUrn, dbId) {
  return `${modelUrn === null || modelUrn === undefined ? '' : modelUrn}${IDENTITY_SEPARATOR}${dbId}`;
}

function compareText(left, right) {
  const a = left === null || left === undefined ? '' : String(left);
  const b = right === null || right === undefined ? '' : String(right);
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * The models currently in the viewer, in the `{ urn, model }` shape the arbitrator binds against.
 *
 * `getSeedUrn()` is the repository's model-identity accessor - `forgeService.js:2991` matches a
 * stored URN against exactly this - and `normalizeModelUrn` is the shared comparison rule (trim,
 * strip trailing '=', preserve case). A model whose seed URN cannot be read is still returned with
 * a null URN so it can serve as the single-model binding target; it just cannot match by name.
 *
 * The dashboard also loads a zero-fragment placeholder model. It has a Forge Model object but can
 * never own an element dbId, so counting it would turn every legitimate URN-less address into a
 * false `ambiguousModel`. We exclude only models whose completed load EXPLICITLY reports zero; an
 * unfinished model or an unknown/temporarily unreadable fragment list stays admitted and therefore
 * keeps arbitration fail-closed rather than being guessed away.
 *
 * @param {object} viewer
 * @returns {Array<{urn: string|null, model: object}>}
 */
export function listLoadedModels(viewer) {
  if (!viewer || typeof viewer.getAllModels !== 'function') {
    return viewer && viewer.model && canOwnElementGeometry(viewer.model)
      ? [{ urn: null, model: viewer.model }]
      : [];
  }

  let models = [];
  try {
    models = viewer.getAllModels() || [];
  } catch (err) {
    warn('[SpatialFocus] getAllModels failed', err);
    return viewer.model && canOwnElementGeometry(viewer.model)
      ? [{ urn: null, model: viewer.model }]
      : [];
  }

  return models.filter((model) => model && canOwnElementGeometry(model)).map((model) => {
    let urn = null;
    try {
      urn = typeof model.getSeedUrn === 'function' ? normalizeModelUrn(model.getSeedUrn()) : null;
    } catch (err) {
      warn('[SpatialFocus] getSeedUrn failed', err);
    }
    return { urn, model };
  });
}

/** Whether this model is capable of owning an element address. Unknown stays conservatively true. */
function canOwnElementGeometry(model) {
  if (!model || typeof model.getFragmentList !== 'function') return true;

  try {
    const fragments = model.getFragmentList();
    if (!fragments) return true;

    if (typeof fragments.getCount === 'function') {
      const count = fragments.getCount();
      if (Number.isFinite(count)) {
        if (count > 0) return true;
        if (typeof model.isLoadDone === 'function' && model.isLoadDone() === false) return true;
        return false;
      }
    }

    const ids = fragments.fragments && fragments.fragments.fragId2dbId;
    if (ids && Number.isFinite(ids.length)) return ids.length > 0;
  } catch (err) {
    warn('[SpatialFocus] fragment list inspection failed', err);
  }

  return true;
}

/** Group element claims into the model-scoped buckets every Forge primitive wants. */
function bucketClaims(claims) {
  const buckets = new Map();

  (claims || []).forEach((claim) => {
    const key = claim.modelUrn === null || claim.modelUrn === undefined ? '' : claim.modelUrn;
    if (!buckets.has(key)) {
      buckets.set(key, { model: claim.model, modelUrn: claim.modelUrn, dbIds: [], claims: [] });
    }
    const bucket = buckets.get(key);
    bucket.dbIds.push(claim.dbId);
    bucket.claims.push(claim);
  });

  return [...buckets.values()].sort((a, b) => compareText(a.modelUrn, b.modelUrn));
}

/**
 * Restore an element's own material on ONE model.
 *
 * `clearPermeativeElementsTheme` (forgeUtils.js:139) is the same call, but it hardcodes
 * `store.state.viewer.model`, so it cannot reach a claim that bound to a second loaded model.
 * This keeps its exact semantics - Forge restores the original material when the colour is
 * `undefined` - while honouring the plan's model.
 */
function restoreMaterial(model, dbId) {
  if (!model || typeof model.setThemingColor !== 'function') return;
  try {
    model.setThemingColor(dbId, undefined, true);
  } catch (err) {
    warn('[SpatialFocus] restore material failed', err);
    return;
  }
  const service = forgeService();
  if (service && service.dbIdPerThemingColor) delete service.dbIdPerThemingColor[dbId];
}

/**
 * Undo exactly what this adapter last painted on this viewer, and nothing else.
 *
 * @param {object} viewer
 * @param {{keepIsolation?: boolean}} [options] keepIsolation: the new scene isolates in this same
 *        synchronous step, so skip the de-isolate - the new isolate replaces the set the way a plain
 *        tag-card isolate does, and the user's ghosting is never touched.
 * @returns {boolean} whether there was an owned scene to clear - so a second clear is a true no-op
 */
export function clearSpatialRender(viewer, { keepIsolation = false } = {}) {
  const ledger = viewer ? ledgerByViewer.get(viewer) : null;
  if (!ledger) return false;

  ledgerByViewer.delete(viewer);

  if (ledger.stopPulse) {
    try {
      ledger.stopPulse();
    } catch (err) {
      warn('[SpatialFocus] pulse stop failed', err);
    }
  }

  ledger.themed.forEach((entry) => restoreMaterial(entry.model, entry.dbId));

  if (ledger.markers > 0) {
    const service = forgeService();
    if (service && typeof service.clearCmlPointMarkers === 'function') {
      service.clearCmlPointMarkers();
    }
  }

  // The existing "stop isolating" primitive, and ONLY when this adapter actually established an
  // isolation (`isolatedModel` is null for a marker-only scene). That precision matters: an empty
  // `isolateDbIds` routes to `endIsolation()`, which is a whole-scene reset - it also resets object
  // materials, drops ghosting, and CLEARS THE SHARED CML MARKER LAYER (mainExtension.js:1486-1499).
  // Undoing an isolation we made is honest; triggering that reset when we never isolated is not.
  const extension = mainExtension();
  if (!keepIsolation && ledger.isolatedModel && extension && typeof extension.isolateDbIds === 'function') {
    try {
      extension.isolateDbIds([], ledger.isolatedModel);
    } catch (err) {
      warn('[SpatialFocus] end isolation failed', err);
    }
  }

  if (viewer && viewer.impl && typeof viewer.impl.invalidate === 'function') {
    viewer.impl.invalidate(true);
  }

  return true;
}

/** The repository's reduced-motion read (SqlViewDataGrid.vue:2135, ViewerTaskColoring.vue:250). */
function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  return !!(query && query.matches);
}

/** Isolate every bucket through the model-aware extension primitive. */
function isolateBuckets(buckets, diagnostics) {
  const extension = mainExtension();
  if (!extension || typeof extension.isolateDbIds !== 'function') return null;

  let isolatedModel = null;
  buckets.forEach((bucket) => {
    try {
      extension.isolateDbIds([...new Set(bucket.dbIds)], bucket.model);
      isolatedModel = bucket.model;
    } catch (err) {
      warn('[SpatialFocus] isolate failed', err);
    }
  });

  if (buckets.length > 1) diagnostics.set(DIAGNOSTIC_MULTI_MODEL, buckets.length - 1);
  return isolatedModel;
}

/** Theme each claim that carries a colour; an uncoloured target stays a plain, natural isolate. */
function themeClaims(viewer, buckets, ledger) {
  let themed = 0;

  buckets.forEach((bucket) => {
    bucket.claims.forEach((claim) => {
      if (!claim.color) return;
      try {
        setThemingColor(viewer, claim.dbId, $hexToVector4(claim.color), claim.model);
      } catch (err) {
        warn('[SpatialFocus] theming failed', err);
        return;
      }
      ledger.themed.set(identityKey(claim.modelUrn, claim.dbId), {
        model: claim.model,
        dbId: claim.dbId,
      });
      themed += 1;
    });
  });

  return themed;
}

/** Whoever currently owns the shared CML marker layer, or null when it is unclaimed. */
function markerLayerOwner() {
  return (store.state.TAGS && store.state.TAGS.cmlMarkerOwner) || null;
}

/**
 * Place the plan's point markers and READ BACK how many survived.
 *
 * `showSpatialPointMarkers` is the subject-aware interface over the repository's shared point-
 * marker renderer, so this
 * function is mostly about what NOT to do to it:
 *
 *   - It clears the whole layer unconditionally (forgeService.js:4556) BEFORE its own display gate,
 *     and that teardown closes any open CML popup (:5113). So a spatial focus must never call it
 *     while somebody else's markers are on screen. Ownership is checked FIRST, through the layer's
 *     existing `TAGS.cmlMarkerOwner` token, and a foreign or unclaimed set makes this refuse
 *     outright rather than destroy it. The rest of the render plan still paints.
 *   - `force: true` would bypass the redundant-re-render skip that keeps an open popup's anchor
 *     alive (:4546-4554) and the user's own `showCml` display preference (:4561) - and because the
 *     clear happens BEFORE that gate, forcing past a disabled toggle would tear the layer down for
 *     nothing. The toggle is therefore pre-checked and honoured, not forced.
 *   - `TAGS.isolatedCmlIds` silently filters every record outside an active CML isolation, and a
 *     point that fails `worldToView3d` is dropped. Neither is knowable in advance, so the count is
 *     taken from the channel's own placed-marker list rather than from the number handed to it.
 *
 * Every refusal and every shortfall is the one typed `markerChannelBlocked` diagnostic.
 *
 * @param {object} plan
 * @param {Map<string,object>} subjectsByKey subjectKey -> the complete stable subject
 * @param {Map} diagnostics
 */
function renderMarkers(plan, subjectsByKey, diagnostics) {
  const markers = plan.markers || [];
  if (!markers.length) return { requested: 0, rendered: 0, refused: null, ids: [] };

  const refuse = (reason) => {
    diagnostics.set(DIAGNOSTIC_MARKER_BLOCKED, markers.length);
    return { requested: markers.length, rendered: 0, refused: reason, ids: [] };
  };

  const service = forgeService();
  if (!service || typeof service.showSpatialPointMarkers !== 'function') return refuse('channelUnavailable');

  // Never destroy a marker set this adapter did not place. An unclaimed but occupied layer counts
  // as foreign: a legacy caller that rendered without claiming ownership still owns what is on
  // screen, and a null token is not permission.
  const occupied = Array.isArray(service._cmlMarkers) && service._cmlMarkers.length > 0;
  if (occupied && markerLayerOwner() !== SPATIAL_FOCUS_MARKER_OWNER) return refuse('foreignMarkerLayer');

  if (!(store.state.userSettings
    && store.state.userSettings.displaySettings
    && store.state.userSettings.displaySettings.showCml)) {
    return refuse('cmlMarkersHidden');
  }

  // The channel keys each mesh by `_cmlId` (forgeService.js:4618) and every lookup - popup
  // resolution, `find3DMarkerByEntity` (:4782), cross-viewer sync - matches on it. So this MUST be
  // the subject's real domain id, not an addressKey, or the marker becomes unaddressable. The
  // addressKey stays in this module's own ledger.
  let ambiguous = 0;
  const markerRecords = markers.map((marker) => {
    const subjectKeys = marker.subjectKeys || [];
    const subject = subjectKeys.length === 1 ? subjectsByKey.get(subjectKeys[0]) : null;
    if (!subject?.id || !subject?.kind) ambiguous += 1;
    return {
      // A MIXED point belongs to two subjects at once, so no single id is true for it. It keeps
      // its address as a distinct, non-colliding key and is reported instead of being given one
      // of the two subjects' ids.
      id: subject?.id || marker.addressKey,
      point: marker.point,
      label: subject?.displayLabel || null,
      subject: subject || null,
    };
  });
  if (ambiguous > 0) diagnostics.set(DIAGNOSTIC_MARKER_SUBJECT_AMBIGUOUS, ambiguous);

  // Keyed by record IDENTITY: two point addresses can resolve to one subject id, so an id-keyed
  // lookup would not be unique.
  const colorByRecord = new Map(markerRecords.map((record, index) => [record, markers[index].color]));

  try {
    service.showSpatialPointMarkers({ markers: markerRecords }, {
      ownerId: SPATIAL_FOCUS_MARKER_OWNER,
      colorResolver: (record) => colorByRecord.get(record) || null,
      onActivate: activateSpatialMarkerSubject,
    });
  } catch (err) {
    warn('[SpatialFocus] marker render failed', err);
    return refuse('renderFailed');
  }

  const placed = Array.isArray(service._cmlMarkers) ? service._cmlMarkers.length : markers.length;
  const rendered = Math.min(placed, markers.length);
  if (rendered < markers.length) {
    diagnostics.set(DIAGNOSTIC_MARKER_BLOCKED, markers.length - rendered);
  }

  return {
    requested: markers.length,
    rendered,
    refused: null,
    ids: markers.map((marker, index) => ({
      addressKey: marker.addressKey,
      id: markerRecords[index].id,
    })),
  };
}

/** Frame the plan's fit targets: elements first, points as the only remaining channel. */
function applyFit(viewer, plan, diagnostics) {
  const fitTargets = plan.fitTargets || { role: null, elements: [], points: [] };
  if (!fitTargets.role) return null;

  const elements = (fitTargets.elements || []).filter((bucket) => bucket.dbIds && bucket.dbIds.length);
  if (elements.length) {
    // One camera move: zoomToFit selects then fits, so a second call would simply discard the
    // first. The primary bucket owns the frame and the multi-model diagnostic already says so.
    try {
      zoomToFit(viewer, elements[0].dbIds, elements[0].model);
      return fitTargets.role;
    } catch (err) {
      warn('[SpatialFocus] element fit failed', err);
      diagnostics.set(DIAGNOSTIC_FIT_UNAVAILABLE, 1);
      return null;
    }
  }

  const points = fitTargets.points || [];
  if (!points.length) return null;

  const service = forgeService();
  if (!service || typeof service.zoomToPoint3dViewer !== 'function'
    || typeof service.worldToView3d !== 'function') {
    diagnostics.set(DIAGNOSTIC_FIT_UNAVAILABLE, 1);
    return null;
  }

  // Points arrive in the GLOBAL frame (coordinateFrame: "global"); the 3D camera works in view
  // space, so they go through the same conversion the CML marker channel uses.
  const viewPoints = points
    .map((point) => service.worldToView3d(point, null))
    .filter((point) => point && Number.isFinite(point.x) && Number.isFinite(point.y)
      && Number.isFinite(point.z));

  if (!viewPoints.length) {
    diagnostics.set(DIAGNOSTIC_FIT_UNAVAILABLE, 1);
    return null;
  }

  // zoomToPoint3dViewer frames a fixed box around ONE point, so several scattered points can only
  // share a centroid. Typed rather than silent: the frame may not contain every marker.
  if (viewPoints.length > 1) diagnostics.set(DIAGNOSTIC_POINT_FIT_APPROXIMATE, viewPoints.length);

  const centroid = viewPoints.reduce(
    (total, point) => ({ x: total.x + point.x, y: total.y + point.y, z: total.z + point.z }),
    { x: 0, y: 0, z: 0 },
  );

  try {
    service.zoomToPoint3dViewer({
      x: centroid.x / viewPoints.length,
      y: centroid.y / viewPoints.length,
      z: centroid.z / viewPoints.length,
    });
  } catch (err) {
    warn('[SpatialFocus] point fit failed', err);
    diagnostics.set(DIAGNOSTIC_FIT_UNAVAILABLE, 1);
    return null;
  }

  return fitTargets.role;
}

/**
 * Start the settled-highlight pulse, one bounded run per MODEL bucket.
 *
 * `startTaskIsolationPulse` hands its callbacks a flat `dbIds` array with no model, so binding the
 * model into the closures is the only way to keep the pulse on the same `(URN, dbId)` identity the
 * paint used. It already owns the reduced-motion gate, the bounded tick count and the restore to
 * the settled colour, so none of that is re-implemented here.
 */
function startPulse(viewer, buckets, options) {
  const reduced = prefersReducedMotion();
  const stops = [];
  let started = false;

  buckets.forEach((bucket) => {
    const byColor = new Map();
    bucket.claims.forEach((claim) => {
      if (claim.state === STATE_CONTEXT) return;
      const key = claim.color || '__natural__';
      if (!byColor.has(key)) {
        // Same tint rule and same constants as the Task pulse plan (taskIsolationPaint.js:176-181):
        // flash to a LIGHTER TINT OF THE ELEMENT'S OWN COLOUR, never to a shared white - two
        // targets with different styles must stay distinguishable in every frame of the flash.
        byColor.set(key, {
          dbIds: [],
          settledColor: claim.color || null,
          flashColor: claim.color
            ? lightenHex(claim.color, TASK_ISOLATION_PULSE_LIGHTEN)
            : TASK_ISOLATION_NATURAL_FLASH_COLOR,
        });
      }
      byColor.get(key).dbIds.push(claim.dbId);
    });

    const groups = [...byColor.values()];
    if (!groups.length) return;

    const stop = startTaskIsolationPulse(groups, {
      setColor: (dbIds, hex) => dbIds.forEach(
        (dbId) => setThemingColor(viewer, dbId, $hexToVector4(hex), bucket.model),
      ),
      clearColor: (dbIds) => {
        dbIds.forEach((dbId) => restoreMaterial(bucket.model, dbId));
        // Removing a theming colour updates Forge's material state, but does not schedule a frame.
        // Without this redraw the last pulse frame can stay visible until hover invalidates the
        // viewer incidentally. One invalidate per settled group keeps the natural colour immediate.
        viewer.impl?.invalidate?.(true);
      },
      setIntervalFn: options.setIntervalFn,
      clearIntervalFn: options.clearIntervalFn,
      prefersReducedMotion: reduced,
    });

    stops.push(stop);
    if (!reduced) started = true;
  });

  return {
    pulsing: started,
    stop: () => stops.forEach((stop) => stop()),
  };
}

/**
 * Paint one render plan and return the receipt of what actually happened.
 *
 * @param {object} viewer the Forge viewer that owns this scene
 * @param {object} plan   `buildSpatialRenderPlan` output
 * @param {object} [options]
 * @param {{setIntervalFn?: Function, clearIntervalFn?: Function}} [options.pulse] the pulse's
 *        timing seam - the same injectable interval pair `startTaskIsolationPulse` already
 *        exposes. Omitted, the real timers are used.
 * @param {Map<string,object>} [options.subjectsByKey] subjectKey -> complete stable subject.
 *        Required for marker identity, label and activation routing.
 * @returns {{
 *   isolated: number, themed: number,
 *   buckets: Array<{modelUrn: string|null, dbIds: number, themed: number}>,
 *   markers: {requested: number, rendered: number, refused: string|null,
 *             ids: Array<{addressKey: string, id: string}>},
 *   fit: 'target'|'context'|null, pulsing: boolean,
 *   diagnostics: Array<{code: string, count: number}>, partial: boolean
 * }}
 */
export function applySpatialRenderPlan(viewer, plan, options = {}) {
  const diagnostics = new Map();
  const emptyReceipt = () => ({
    isolated: 0,
    themed: 0,
    buckets: [],
    markers: { requested: 0, rendered: 0, refused: null, ids: [] },
    fit: null,
    pulsing: false,
    diagnostics: [...diagnostics.entries()].map(([code, count]) => ({ code, count })),
    partial: diagnostics.size > 0,
  });

  if (!viewer || !plan) return emptyReceipt();

  // A new scene never accumulates on the previous one.
  clearSpatialRender(viewer);

  const buckets = bucketClaims(plan.elementClaims);
  if (!buckets.length && !(plan.markers || []).length) return emptyReceipt();

  // Drop any theming a foreign paint left on the primary model, exactly as the existing coloured
  // isolate does (forgeUtils.js:1297). The per-identity ledger above is what reaches the others.
  try {
    clearThemingColors(viewer);
  } catch (err) {
    warn('[SpatialFocus] theming reset failed', err);
  }

  const ledger = {
    themed: new Map(),
    markers: 0,
    // addressKey -> the id actually handed to the marker channel. The addressKey lives HERE and
    // nowhere else: the channel is keyed by the subject's real domain id.
    markerIds: new Map(),
    stopPulse: null,
    isolatedModel: null,
  };
  ledgerByViewer.set(viewer, ledger);

  ledger.isolatedModel = isolateBuckets(buckets, diagnostics);
  const themed = themeClaims(viewer, buckets, ledger);

  const markers = renderMarkers(
    plan,
    options.subjectsByKey || new Map(),
    diagnostics,
  );
  ledger.markers = markers.rendered;
  markers.ids.forEach((entry) => ledger.markerIds.set(entry.addressKey, entry.id));

  const fit = applyFit(viewer, plan, diagnostics);

  const pulse = startPulse(viewer, buckets, options.pulse || {});
  ledger.stopPulse = pulse.stop;

  const isolated = buckets.reduce((total, bucket) => total + new Set(bucket.dbIds).size, 0);

  return {
    isolated,
    themed,
    buckets: buckets.map((bucket) => ({
      modelUrn: bucket.modelUrn,
      dbIds: new Set(bucket.dbIds).size,
      themed: bucket.claims.filter((claim) => !!claim.color).length,
    })),
    markers,
    fit,
    pulsing: pulse.pulsing,
    diagnostics: [...diagnostics.entries()]
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => compareText(a.code, b.code)),
    // The plan asked for something this render could not deliver. Kept separate from the plan's
    // own `partial` so the legend can tell "the server lost it" from "the viewer refused it".
    partial: diagnostics.size > 0,
  };
}

/**
 * Frame a render plan's fit targets and change nothing else: no isolation, theming, markers or
 * pulse. The camera move is the scene's own (`applyFit`), so a zoom to several tags frames them
 * exactly as an isolate of the same tags would (platform/viewer scene.js `frameScope`).
 *
 * @param {Object} viewer
 * @param {Object} plan a `buildSpatialRenderPlan` result
 * @returns {{fit: ('target'|'context'|null), diagnostics: Array<{code: string, count: number}>}}
 */
export function fitSpatialRenderPlan(viewer, plan) {
  const diagnostics = new Map();
  const fit = viewer && plan ? applyFit(viewer, plan, diagnostics) : null;
  return {
    fit,
    diagnostics: [...diagnostics.entries()]
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => compareText(a.code, b.code)),
  };
}
